// PICKUP-REFORGE "Resonance Relics v2" (P1 2026-11-02, Docs/DESIGN/PICKUP_REDESIGN_P1_v2.md). Render-only.
// XP Splinter / Fork / Crown, Vital Salve and Sweep Compass: procedural flat-faceted cel meshes with ink hulls, one instanced
// mesh per type, terrain-following ground marks, thin locator beacons and pooled collection VFX. The Array Seal stays on the
// classic gem path in view.ts. Reads Sim state only (collections are detected as alive → dead transitions); never writes it.
// ?pickups=classic keeps the old octahedra for comparison.
// XP TIERS BY VALUE (owner 2026-11-02, src/render/gemTier.ts): Small royal blue, Medium violet, Large magenta (2026-11-04, bright maps), Huge (a Large gem worth 40+ XP,
// render-only) red with a gold glint. Palette colours come from a11y.ts (colour-blind modes swap them live); every tier also differs in
// silhouette, size and glow ring so value never rests on hue alone.
import * as THREE from 'three/webgpu';
import { attribute, cos, dot, float, floor, fract, instancedBufferAttribute, mix, normalView, normalWorld, positionGeometry, positionLocal, positionViewDirection, pow, sin, smoothstep, step, texture, time, uniform, uv, vec2, vec3 } from 'three/tsl';
import { GEM_POOL, type Sim } from '../core/sim';
import { CB, FLASH } from './a11y';
import { TIER_COUNT, type Shades, tierColor, tierShades, xpTier } from './gemTier';
import { RenderStyle, toonMaterial } from './toon';

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion();
const _e = new THREE.Euler(), _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Color();
const _X = new THREE.Vector3(1, 0, 0), _Y = new THREE.Vector3(0, 1, 0), _z = new THREE.Vector3(0, 0, 1);
const TAU = Math.PI * 2;

// ---------------------------------------------------------------- geometry recipes ----------------------------------------------------------------
export type V3 = [number, number, number];
/** [colour, emissive 0..1] for one facet */
export type Paint = [number, number];

/** Non-indexed flat-faceted builder: every triangle gets its own normal, vertex colour, emissive weight and spin rate. */
export class Facets {
  pos: number[] = []; col: number[] = []; emis: number[] = []; spin: number[] = [];
  tris = 0;
  /** add a triangle; it is wound so its normal points away from `ctr` */
  tri(a: V3, b: V3, c: V3, paint: Paint, ctr: V3, spin = 0): void {
    _a.set(b[0] - a[0], b[1] - a[1], b[2] - a[2]); _b.set(c[0] - a[0], c[1] - a[1], c[2] - a[2]); _a.cross(_b);
    const mx = (a[0] + b[0] + c[0]) / 3 - ctr[0], my = (a[1] + b[1] + c[1]) / 3 - ctr[1], mz = (a[2] + b[2] + c[2]) / 3 - ctr[2];
    if (_a.x * mx + _a.y * my + _a.z * mz < 0) { const t = b; b = c; c = t; }
    _c.setHex(paint[0]);
    for (const v of [a, b, c]) { this.pos.push(v[0], v[1], v[2]); this.col.push(_c.r, _c.g, _c.b); this.emis.push(paint[1]); this.spin.push(spin); }
    this.tris++;
  }
  /** connect two rings of equal length with quads */
  loft(r0: V3[], r1: V3[], paint: (k: number) => Paint, ctr: V3, spin = 0): void {
    const n = r0.length;
    for (let k = 0; k < n; k++) {
      const k1 = (k + 1) % n, p = paint(k);
      this.tri(r0[k], r0[k1], r1[k1], p, ctr, spin); this.tri(r0[k], r1[k1], r1[k], p, ctr, spin);
    }
  }
  /** fan a ring to an apex */
  fan(r: V3[], apex: V3, paint: (k: number) => Paint, ctr: V3, spin = 0): void {
    for (let k = 0; k < r.length; k++) this.tri(r[k], r[(k + 1) % r.length], apex, paint(k), ctr, spin);
  }
  /** flat polygon cap (fan from vertex 0) */
  cap(r: V3[], paint: Paint, ctr: V3, spin = 0): void {
    for (let k = 1; k + 1 < r.length; k++) this.tri(r[0], r[k], r[k + 1], paint, ctr, spin);
  }
  /** oriented box: centre, half sizes, yaw about Y (12 tris) */
  box(c: V3, h: V3, yaw: number, paint: (face: number) => Paint, spin = 0): void {
    const cy = Math.cos(yaw), sy = Math.sin(yaw);
    const P = (x: number, y: number, z: number): V3 => [c[0] + x * cy + z * sy, c[1] + y, c[2] - x * sy + z * cy];
    const lo = [P(-h[0], -h[1], -h[2]), P(h[0], -h[1], -h[2]), P(h[0], -h[1], h[2]), P(-h[0], -h[1], h[2])];
    const hi = [P(-h[0], h[1], -h[2]), P(h[0], h[1], -h[2]), P(h[0], h[1], h[2]), P(-h[0], h[1], h[2])];
    this.loft(lo, hi, (k) => paint(k), c, spin);
    this.cap(hi, paint(4), c, spin); this.cap(lo, paint(5), c, spin);
  }
  build(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    g.setAttribute('emis', new THREE.Float32BufferAttribute(this.emis, 1));
    g.setAttribute('spin', new THREE.Float32BufferAttribute(this.spin, 1));
    g.computeVertexNormals(); g.computeBoundingSphere();
    return g;
  }
}

export const ring = (n: number, r: number, y: number, sx = 1, sz = 1, ox = 0, oz = 0, a0 = 0): V3[] =>
  Array.from({ length: n }, (_, k) => { const a = a0 + (k / n) * TAU; return [ox + Math.cos(a) * r * sx, y, oz + Math.sin(a) * r * sz] as V3; });

/** a tapered crystal: base ring → shoulder ring → apex, leaning from base (bx,by,bz) to apex (ax,ay,az) */
export function crystal(f: Facets, sides: number, base: V3, apex: V3, r0: number, r1: number, shoulder: number, depthK: number, paint: (k: number, upper: boolean) => Paint, a0 = 0): void {
  const sh: V3 = [base[0] + (apex[0] - base[0]) * shoulder, base[1] + (apex[1] - base[1]) * shoulder, base[2] + (apex[2] - base[2]) * shoulder];
  const rb = ring(sides, r0, base[1], 1, depthK, base[0], base[2], a0), rs = ring(sides, r1, sh[1], 1, depthK, sh[0], sh[2], a0);
  const ctr: V3 = [(base[0] + apex[0]) / 2, (base[1] + apex[1]) / 2, (base[2] + apex[2]) / 2];
  f.loft(rb, rs, (k) => paint(k, false), ctr);
  f.fan(rs, apex, (k) => paint(k, true), ctr);
}

export interface PickupLook { geo: THREE.BufferGeometry; ink: number; hover: number; bob: number; bobHz: number; yaw: number; tilt: number; pulse: number; pulseHz: number; tris: number }

/** XP Small "Resonance Splinter": asymmetric 4-sided shard, long upper point, side tooth, blunt bottom. 0.36 × 0.19 × 0.13 m */
function splinter(sh: Shades): PickupLook {
  const BODY = sh.body, LIGHT = sh.light, DARK = sh.dark;
  const f = new Facets(), C: V3 = [0, 0.02, 0];
  const mid = ring(4, 0.075, -0.01, 1, 0.87, 0, 0, Math.PI / 4).map((v) => [v[0] - 0.012, v[1] + v[0] * 0.25, v[2]] as V3);
  const low = ring(4, 0.03, -0.125, 1, 0.87, -0.012, 0, Math.PI / 4);
  const top: V3 = [0.012, 0.235, 0.005];
  f.fan(mid, top, (k) => (k % 2 ? [LIGHT, 0.55] : [BODY, 0.35]), C);
  f.loft(mid, low, (k) => (k % 2 ? [BODY, 0.25] : [DARK, 0.1]), C);
  f.cap(low, [DARK, 0.05], C);
  // side tooth: 3-sided spike off the +x shoulder
  const tb: V3[] = [[0.045, 0.0, 0.03], [0.05, 0.055, 0.0], [0.045, 0.0, -0.03]];
  f.fan(tb, [0.115, 0.12, 0.0], (k) => (k === 1 ? [LIGHT, 0.55] : [BODY, 0.35]), [0.03, 0.03, 0]);
  return { geo: f.build(), ink: sh.ink, hover: 0.46, bob: 0.045, bobHz: 1.45, yaw: 1.1, tilt: 8 * Math.PI / 180, pulse: 0.08, pulseHz: 1.7, tris: f.tris };
}

/** XP Medium "Resonance Fork": two unequal prongs out of a dark 6-sided copper socket, clear V gap. 0.50 × 0.38 × 0.20 m */
function fork(sh: Shades): PickupLook {
  const BODY = sh.body, LIGHT = sh.light, DARK = sh.dark, SOCK = sh.ink, SOCK_HI = 0x8a6a48;
  const f = new Facets();
  // socket: 6-sided frustum, bottom −0.20 → top −0.10
  const sb = ring(6, 0.06, -0.2, 1, 0.8), st = ring(6, 0.095, -0.1, 1, 0.8);
  f.loft(sb, st, (k) => (k % 2 ? [SOCK, 0] : [SOCK_HI, 0]), [0, -0.15, 0]);
  f.cap(sb, [SOCK, 0], [0, -0.15, 0]); f.cap(st, [SOCK_HI, 0], [0, -0.15, 0]);
  const pp = (k: number, up: boolean): Paint => (up ? (k % 2 ? [LIGHT, 0.6] : [BODY, 0.35]) : (k % 2 ? [BODY, 0.3] : [DARK, 0.12]));
  crystal(f, 4, [-0.035, -0.11, 0], [-0.13, 0.3, 0.0], 0.05, 0.062, 0.55, 0.85, pp, Math.PI / 4);   // tall left prong
  crystal(f, 4, [0.035, -0.11, 0], [0.18, 0.17, 0.0], 0.045, 0.055, 0.5, 0.85, pp, Math.PI / 4);     // short right prong
  return { geo: f.build(), ink: sh.ink, hover: 0.54, bob: 0.055, bobHz: 1.25, yaw: -0.85, tilt: 0, pulse: 0.1, pulseHz: 1.4, tris: f.tris };
}

/** XP Large "Resonance Crown": three 5-sided prisms (centre tallest) over a broken triangular gold frame that counter-spins. 0.69 × 0.62 × 0.27 m */
function crown(sh: Shades): PickupLook {
  const BODY = sh.body, LIGHT = sh.light, GOLD = sh.light, DARK = sh.dark, COPPER = sh.dark; // (the frame is the tier's own light/dark: gold is kept for the Huge tier)
  const f = new Facets();
  const pp = (k: number, up: boolean): Paint => (up ? (k % 2 ? [LIGHT, 0.65] : [BODY, 0.4]) : (k === 0 ? [GOLD, 0.25] : k % 2 ? [BODY, 0.3] : [DARK, 0.1]));
  crystal(f, 5, [0, -0.2, 0], [0, 0.36, 0], 0.07, 0.085, 0.5, 0.8, pp);                        // centre
  crystal(f, 5, [-0.06, -0.2, 0], [-0.25, 0.2, 0.02], 0.06, 0.07, 0.5, 0.8, pp, 0.6);           // left
  crystal(f, 5, [0.06, -0.2, 0], [0.25, 0.2, -0.02], 0.06, 0.07, 0.5, 0.8, pp, 0.3);           // right
  // broken triangular frame (three gapped bars), spinning −0.35 rad/s relative to the world (−1.0 relative to the +0.65 body yaw)
  const R = 0.19, spin = -1.0;
  for (let k = 0; k < 3; k++) {
    const a = (k / 3) * TAU + Math.PI / 2, cx = Math.cos(a + Math.PI / 3) * R * 0.5, cz = Math.sin(a + Math.PI / 3) * R * 0.5;
    f.box([cx, -0.3, cz], [0.13, 0.025, 0.022], -(a + Math.PI / 3) + Math.PI / 2, (fc) => (fc === 4 ? [GOLD, 0.3] : fc === 5 ? [COPPER, 0] : [COPPER, 0.05]), spin);
  }
  return { geo: f.build(), ink: sh.ink, hover: 0.63, bob: 0.065, bobHz: 1.05, yaw: 0.65, tilt: 0, pulse: 0.12, pulseHz: 1.2, tris: f.tris };
}

/** XP Huge "Resonance Spire" (render-only 4th tier, a Large gem worth 40+ XP): a tall six-sided crystal ringed by four leaning shards, a
 *  counter-spinning gold hexagon ring and a gold diamond that turns above the tip (the gold glint). 0.92 × 0.60 × 0.60 m, ~150 tris */
function spire(sh: Shades): PickupLook {
  const BODY = sh.body, LIGHT = sh.light, DARK = sh.dark, GOLD = 0xffd24a, GOLD_HI = 0xfff2a8, GOLD_D = 0xa8741c;
  const f = new Facets();
  const big = (k: number, up: boolean): Paint => (up ? (k % 2 ? [LIGHT, 0.7] : [BODY, 0.5]) : (k === 0 ? [GOLD, 0.45] : k % 2 ? [BODY, 0.35] : [DARK, 0.12]));
  const small = (k: number, up: boolean): Paint => (up ? (k % 2 ? [LIGHT, 0.65] : [BODY, 0.45]) : (k % 2 ? [BODY, 0.3] : [DARK, 0.1]));
  crystal(f, 6, [0, -0.26, 0], [0, 0.46, 0], 0.085, 0.115, 0.5, 0.88, big);
  for (let k = 0; k < 4; k++) { // four shards leaning out from the foot, like a bud
    const a = (k / 4) * TAU + Math.PI / 4;
    crystal(f, 4, [Math.cos(a) * 0.09, -0.26, Math.sin(a) * 0.09], [Math.cos(a) * 0.3, 0.1, Math.sin(a) * 0.3], 0.045, 0.052, 0.5, 0.88, small, a);
  }
  // gold ring: six short bars round the foot (a hexagon with gaps), spinning against the body yaw
  const R = 0.3, spin = -1.1;
  for (let k = 0; k < 6; k++) {
    const a = (k / 6) * TAU, cx = Math.cos(a) * R, cz = Math.sin(a) * R;
    f.box([cx, -0.3, cz], [0.105, 0.022, 0.026], -a + Math.PI / 2, (fc) => (fc === 4 ? [GOLD_HI, 0.55] : fc === 5 ? [GOLD_D, 0] : [GOLD, 0.35]), spin);
  }
  // gold glint: a small diamond turning above the tip
  const gc: V3 = [0, 0.66, 0], gr = ring(4, 0.06, 0.66, 1, 1);
  f.fan(gr, [0, 0.74, 0], (k) => (k % 2 ? [GOLD_HI, 0.9] : [GOLD, 0.8]), gc, 2.2);
  f.fan(gr, [0, 0.58, 0], (k) => (k % 2 ? [GOLD, 0.7] : [GOLD_D, 0.5]), gc, 2.2);
  return { geo: f.build(), ink: sh.ink, hover: 0.7, bob: 0.075, bobHz: 0.95, yaw: 0.5, tilt: 0, pulse: 0.14, pulseHz: 1.1, tris: f.tris };
}

/** Vital Salve: crimson hex capsule in a 4-rail copper cage on a base plate, ivory cross cap on top. 0.77 × 0.58 × 0.47 m */
function salve(_sh?: Shades): PickupLook {
  const BODY = 0xd9435c, INNER = 0xff8791, IVORY = 0xfff2d7, CAGE = 0xb77b49, INK = 0x552837, CAGE_D = 0x6e4630;
  const f = new Facets(), C: V3 = [0, 0, 0];
  // capsule: hex prism −0.16 → 0.12 with pyramid caps
  const lo = ring(6, 0.17, -0.14, 1, 0.85), hi = ring(6, 0.17, 0.07, 1, 0.85);
  f.loft(lo, hi, (k) => (k % 2 ? [INNER, 0.45] : [BODY, 0.3]), C);
  f.fan(hi, [0, 0.16, 0], (k) => (k % 2 ? [INNER, 0.4] : [BODY, 0.25]), C);
  f.fan(lo, [0, -0.24, 0], () => [BODY, 0.15], C);
  // base plate: hex frustum −0.39 → −0.31
  const pb = ring(6, 0.22, -0.39, 1, 0.82, 0, 0, Math.PI / 6), pt = ring(6, 0.25, -0.31, 1, 0.82, 0, 0, Math.PI / 6);
  f.loft(pb, pt, () => [CAGE_D, 0], [0, -0.35, 0]); f.cap(pt, [CAGE, 0], [0, -0.35, 0]); f.cap(pb, [CAGE_D, 0], [0, -0.35, 0]);
  // four cage rails from the plate up to the cross arms
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * TAU;
    f.box([Math.cos(a) * 0.235, -0.065, Math.sin(a) * 0.235 * 0.85], [0.022, 0.245, 0.022], -a, (fc) => (fc === 4 ? [CAGE, 0] : fc % 2 ? [CAGE, 0.05] : [CAGE_D, 0]));
  }
  // ivory cross cap: two crossing bars that rest on the rails
  f.box([0, 0.225, 0], [0.27, 0.045, 0.088], 0, (fc) => (fc === 4 ? [IVORY, 0.4] : [IVORY, 0.15]));
  f.box([0, 0.225, 0], [0.088, 0.045, 0.24], 0, (fc) => (fc === 4 ? [IVORY, 0.4] : [IVORY, 0.15]));
  // tipped 15° toward the camera (like the compass) so the cross cap reads as a cross from the 55° gameplay view
  return { geo: f.build(), ink: INK, hover: 0.7, bob: 0.07, bobHz: 0.85, yaw: 0.4, tilt: 15 * Math.PI / 180, pulse: 0.05, pulseHz: 0.85, tris: f.tris };
}

/** Sweep Compass: open 8-sided bronze ring, four long diamond arms, teal aperture, orbiting pointer. Ø 0.91 m, 0.10 thick */
function compass(_sh?: Shades): PickupLook {
  const BODY = 0xd7a64e, RIM = 0xffe3a0, BRONZE = 0x5d4934, AP = 0x37d8ce;
  const f = new Facets(), C: V3 = [0, 0, 0];
  // ring segments (8 sides): top/bottom/outer/inner quads
  const N = 8, ro = 0.3, ri = 0.235, h = 0.05;
  const oT = ring(N, ro, h, 1, 1, 0, 0, Math.PI / 8), oB = ring(N, ro, -h, 1, 1, 0, 0, Math.PI / 8);
  const iT = ring(N, ri, h, 1, 1, 0, 0, Math.PI / 8), iB = ring(N, ri, -h, 1, 1, 0, 0, Math.PI / 8);
  for (let k = 0; k < N; k++) {
    const k1 = (k + 1) % N, a = ((k + 0.5) / N) * TAU + Math.PI / 8, mc: V3 = [Math.cos(a) * (ro + ri) / 2, 0, Math.sin(a) * (ro + ri) / 2];
    const top: Paint = k % 2 ? [RIM, 0.25] : [BODY, 0.15];
    f.tri(iT[k], oT[k], oT[k1], top, [mc[0], -1, mc[2]]); f.tri(iT[k], oT[k1], iT[k1], top, [mc[0], -1, mc[2]]);
    f.tri(iB[k], oB[k], oB[k1], [BRONZE, 0], [mc[0], 1, mc[2]]); f.tri(iB[k], oB[k1], iB[k1], [BRONZE, 0], [mc[0], 1, mc[2]]);
    f.tri(oT[k], oB[k], oB[k1], [BODY, 0.1], C); f.tri(oT[k], oB[k1], oT[k1], [BODY, 0.1], C);
    f.tri(iT[k], iB[k], iB[k1], [BRONZE, 0], [mc[0] * 3, 0, mc[2] * 3]); f.tri(iT[k], iB[k1], iT[k1], [BRONZE, 0], [mc[0] * 3, 0, mc[2] * 3]);
  }
  // four cardinal diamond arms (flattened octahedra) from r 0.2 to 0.455
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * TAU, cx = Math.cos(a), cz = Math.sin(a), px = -cz, pz = cx;
    const inner: V3 = [cx * 0.2, 0, cz * 0.2], outer: V3 = [cx * 0.455, 0, cz * 0.455];
    const mid = 0.31, w = 0.075, th = 0.07;
    const L: V3 = [cx * mid + px * w, 0, cz * mid + pz * w], Rr: V3 = [cx * mid - px * w, 0, cz * mid - pz * w];
    const T: V3 = [cx * mid, th, cz * mid], B: V3 = [cx * mid, -th, cz * mid], ac: V3 = [cx * mid, 0, cz * mid];
    for (const [p0, p1] of [[inner, L], [L, outer], [outer, Rr], [Rr, inner]] as [V3, V3][]) {
      f.tri(p0, p1, T, p1 === outer || p0 === outer ? [RIM, 0.3] : [BODY, 0.2], ac);
      f.tri(p0, p1, B, [BRONZE, 0], ac);
    }
  }
  // teal aperture: flattened hex bipyramid
  const ap = ring(6, 0.11, 0, 1, 1);
  f.fan(ap, [0, 0.07, 0], (k) => (k % 2 ? [AP, 0.75] : [0x9ff8ee, 0.85]), C);
  f.fan(ap, [0, -0.05, 0], () => [0x1d7f7a, 0.2], C);
  // orbiting triangular pointer between aperture and ring (spins −1.2 rad/s in the world = −1.7 relative to the +0.5 yaw)
  const pt: V3[] = [[0.215, 0.025, 0], [0.135, 0.025, 0.035], [0.135, 0.025, -0.035]], pb: V3[] = pt.map((v) => [v[0], -0.015, v[2]] as V3);
  const pc: V3 = [0.16, 0.005, 0];
  f.cap(pt, [RIM, 0.5], pc, -1.7); f.cap(pb, [BRONZE, 0], pc, -1.7); f.loft(pt, pb, () => [BODY, 0.2], pc, -1.7);
  return { geo: f.build(), ink: 0x263b42, hover: 0.72, bob: 0.065, bobHz: 0.95, yaw: 0.5, tilt: 18 * Math.PI / 180, pulse: 0.1, pulseHz: 1.0, tris: f.tris };
}

/** smoothed-normal twin for the inverted-hull ink pass (keeps the spin attribute so moving parts stay inked) */
export function hullGeometry(src: THREE.BufferGeometry): THREE.BufferGeometry {
  const p = src.getAttribute('position'), sp = src.getAttribute('spin'), n = p.count;
  const acc = new Map<string, [number, number, number]>(), key = (i: number) => `${p.getX(i).toFixed(4)},${p.getY(i).toFixed(4)},${p.getZ(i).toFixed(4)},${sp.getX(i)}`;
  const nrm = new Float32Array(n * 3);
  for (let t = 0; t < n; t += 3) {
    _a.fromBufferAttribute(p, t + 1).sub(_b.fromBufferAttribute(p, t)); _p.fromBufferAttribute(p, t + 2).sub(_b);
    _a.cross(_p); // area-weighted face normal
    for (let j = 0; j < 3; j++) { const k = key(t + j), v = acc.get(k) ?? [0, 0, 0]; v[0] += _a.x; v[1] += _a.y; v[2] += _a.z; acc.set(k, v); }
  }
  for (let i = 0; i < n; i++) { const v = acc.get(key(i))!; _a.set(v[0], v[1], v[2]).normalize(); nrm[i * 3] = _a.x; nrm[i * 3 + 1] = _a.y; nrm[i * 3 + 2] = _a.z; }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', p.clone()); g.setAttribute('spin', sp.clone()); g.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  g.computeBoundingSphere();
  return g;
}

/**
 * positionNode for a pickup body (extrude 0) or its ink hull. three applies the instance matrix to positionLocal BEFORE
 * positionNode runs, so spin-tagged vertices (crown frame, compass pointer) are rotated about local Y in model space and only
 * the resulting offset is carried into instance space with the per-instance 'inst' = (cos yaw, sin yaw, scale) and the look's
 * constant world-X tilt. The hull extrusion rides the same path, so moving parts stay inked. Spin 0 + extrude 0 = no offset.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function pickupPosition(extrude: number, tiltX: number, comps: 3 | 4 = 3): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sp = attribute('spin', 'float'), a = time.mul(sp), c: any = cos(a), s: any = sin(a), p: any = positionGeometry, I: any = attribute('inst', comps === 4 ? 'vec4' : 'vec3');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rot = (v: any) => vec3(v.x.mul(c).add(v.z.mul(s)), v.y, v.z.mul(c).sub(v.x.mul(s))); // three's +Y rotation by spin·t
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let off: any = rot(p).sub(p);
  if (extrude) off = off.add(rot(attribute('normal', 'vec3')).mul(float(extrude)));
  off = off.mul(I.z);
  // yaw (three's Y rotation): x' = x cos + z sin, z' = −x sin + z cos
  const x1 = off.x.mul(I.x).add(off.z.mul(I.y)), z1 = off.z.mul(I.x).sub(off.x.mul(I.y));
  if (!tiltX) return positionLocal.add(vec3(x1, off.y, z1));
  const ct = Math.cos(tiltX), st = Math.sin(tiltX);
  return positionLocal.add(vec3(x1, off.y.mul(ct).sub(z1.mul(st)), off.y.mul(st).add(z1.mul(ct))));
}

// ---------------------------------------------------------------- atlas (marks, beacons, VFX) ----------------------------------------------------------------
/** 4×3 atlas, 256 px cells: 0 fork chevrons · 1 crown triangle strokes · 2 salve ring + cross · 3 compass rose · 4 beacon taper · 5 sliver · 6 cross flash · 7 compass silhouette
 *  · 8 glow pool (soft disc + bright inked rim) · 9 four-point glint · 10 spire hexagon strokes. The XP cells are white and take the tier colour from the instance tint. */
/** ground glow pools / glyphs under XP gems (owner 2026-11-03: off) */
export const GEM_GROUND_MARKS = false;
const CELL = { chevron: 0, tri: 1, salve: 2, rose: 3, beacon: 4, sliver: 5, cross: 6, compass: 7, pool: 8, star: 9, hex: 10 } as const;
const ATLAS_ROWS = 3;
function atlasTexture(): THREE.Texture {
  const S = 256, c = document.createElement('canvas'); c.width = S * 4; c.height = S * ATLAS_ROWS; const g = c.getContext('2d')!;
  g.lineCap = 'round'; g.lineJoin = 'round';
  const at = (cell: number, fn: () => void) => { g.save(); g.translate((cell % 4) * S + S / 2, Math.floor(cell / 4) * S + S / 2); fn(); g.restore(); };
  /** ink underlay then colour stroke, so marks read on light stone and dark scorch alike */
  const inked = (path: () => void, w: number, col: string, ink = 'rgba(10,28,36,0.9)') => {
    g.strokeStyle = ink; g.lineWidth = w + 14; g.beginPath(); path(); g.stroke();
    g.strokeStyle = col; g.lineWidth = w; g.beginPath(); path(); g.stroke();
  };
  const R = S * 0.42;
  at(CELL.chevron, () => { // a pair of chevrons pointing in at the drop
    for (const s of [-1, 1]) inked(() => { g.moveTo(s * R, -R * 0.55); g.lineTo(s * R * 0.42, 0); g.lineTo(s * R, R * 0.55); }, 20, '#ffffff');
  });
  at(CELL.tri, () => { // three broken triangle strokes (gold corners)
    for (let k = 0; k < 3; k++) {
      const a0 = -Math.PI / 2 + (k / 3) * TAU, a1 = a0 + TAU / 3, P = (a: number) => [Math.cos(a) * R, Math.sin(a) * R];
      const [x0, y0] = P(a0), [x1, y1] = P(a1);
      inked(() => { g.moveTo(x0 + (x1 - x0) * 0.14, y0 + (y1 - y0) * 0.14); g.lineTo(x0 + (x1 - x0) * 0.86, y0 + (y1 - y0) * 0.86); }, 20, '#ffffff');
    }
  });
  at(CELL.salve, () => { // broken ring + central cross
    for (let k = 0; k < 4; k++) inked(() => g.arc(0, 0, R * 0.9, (k / 4) * TAU + 0.22, ((k + 1) / 4) * TAU - 0.22), 18, '#ff5a72', 'rgba(50,10,20,0.9)');
    g.fillStyle = 'rgba(50,10,20,0.95)'; g.fillRect(-R * 0.42, -R * 0.15, R * 0.84, R * 0.3); g.fillRect(-R * 0.15, -R * 0.42, R * 0.3, R * 0.84);
    g.fillStyle = '#fff2d7'; g.fillRect(-R * 0.36, -R * 0.09, R * 0.72, R * 0.18); g.fillRect(-R * 0.09, -R * 0.36, R * 0.18, R * 0.72);
  });
  at(CELL.rose, () => { // compass rose: inked gold ring broken at the cardinals + four filled teal arrowheads pointing in
    for (let k = 0; k < 4; k++) inked(() => g.arc(0, 0, R * 0.7, (k / 4) * TAU + 0.32, ((k + 1) / 4) * TAU - 0.32), 16, '#ffd779', 'rgba(20,30,34,0.92)');
    for (let k = 0; k < 4; k++) {
      g.save(); g.rotate((k / 4) * TAU);
      const head = () => { g.beginPath(); g.moveTo(R * 0.5, 0); g.lineTo(R * 0.98, -R * 0.2); g.lineTo(R * 0.88, 0); g.lineTo(R * 0.98, R * 0.2); g.closePath(); };
      g.strokeStyle = 'rgba(20,30,34,0.92)'; g.lineWidth = 12; head(); g.stroke();
      g.fillStyle = '#37d8ce'; head(); g.fill(); g.restore();
    }
  });
  at(CELL.beacon, () => { // narrow tapered vertical stroke, bright at the base, fading up
    const grd = g.createLinearGradient(0, R, 0, -R); grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(0.6, 'rgba(255,255,255,0.55)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.beginPath(); g.moveTo(-S * 0.16, R); g.lineTo(-S * 0.02, -R); g.lineTo(S * 0.02, -R); g.lineTo(S * 0.16, R); g.closePath(); g.fill();
  });
  at(CELL.sliver, () => { // thin diamond sliver along +x
    // fills the cell (quad scale sets the aspect): a lens-shaped diamond with a soft halo
    g.shadowColor = 'rgba(255,255,255,0.8)'; g.shadowBlur = 10;
    g.fillStyle = '#ffffff'; g.beginPath(); g.moveTo(-S * 0.46, 0); g.lineTo(0, -S * 0.4); g.lineTo(S * 0.46, 0); g.lineTo(0, S * 0.4); g.closePath(); g.fill();
    g.shadowBlur = 0;
  });
  at(CELL.cross, () => { // ivory cross with a crimson rim
    g.fillStyle = '#d9435c'; g.fillRect(-R * 0.75, -R * 0.27, R * 1.5, R * 0.54); g.fillRect(-R * 0.27, -R * 0.75, R * 0.54, R * 1.5);
    g.fillStyle = '#fff2d7'; g.fillRect(-R * 0.62, -R * 0.15, R * 1.24, R * 0.3); g.fillRect(-R * 0.15, -R * 0.62, R * 0.3, R * 1.24);
  });
  at(CELL.compass, () => { // compass silhouette (ring + four arms) for the Sweep after-image
    inked(() => g.arc(0, 0, R * 0.5, 0, TAU), 16, '#ffe3a0');
    g.fillStyle = '#d7a64e';
    for (let k = 0; k < 4; k++) { g.save(); g.rotate((k / 4) * TAU); g.beginPath(); g.moveTo(R * 0.3, 0); g.lineTo(R * 0.62, -R * 0.13); g.lineTo(R, 0); g.lineTo(R * 0.62, R * 0.13); g.closePath(); g.fill(); g.restore(); }
    g.fillStyle = '#37d8ce'; g.beginPath(); g.arc(0, 0, R * 0.2, 0, TAU); g.fill();
  });
  at(CELL.pool, () => { // soft glow disc with a bright inked rim: the 'pop' under a gem on any ground
    const grd = g.createRadialGradient(0, 0, 0, 0, 0, R * 1.1);
    grd.addColorStop(0, 'rgba(255,255,255,0.5)'); grd.addColorStop(0.7, 'rgba(255,255,255,0.28)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.beginPath(); g.arc(0, 0, R * 1.1, 0, TAU); g.fill();
    inked(() => g.arc(0, 0, R * 0.8, 0, TAU), 15, '#ffffff', 'rgba(8,16,24,0.85)');
  });
  at(CELL.star, () => { // four-point glint: long vertical and horizontal needles, short diagonals
    g.fillStyle = '#ffffff';
    for (const [len, w, rot] of [[R * 1.05, 0.07, 0], [R * 1.05, 0.07, Math.PI / 2], [R * 0.6, 0.05, Math.PI / 4], [R * 0.6, 0.05, -Math.PI / 4]] as [number, number, number][]) {
      g.save(); g.rotate(rot); g.beginPath(); g.moveTo(-len, 0); g.lineTo(0, -len * w * 2.2); g.lineTo(len, 0); g.lineTo(0, len * w * 2.2); g.closePath(); g.fill(); g.restore();
    }
    const grd = g.createRadialGradient(0, 0, 0, 0, 0, R * 0.3); grd.addColorStop(0, 'rgba(255,255,255,0.9)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.beginPath(); g.arc(0, 0, R * 0.3, 0, TAU); g.fill();
  });
  at(CELL.hex, () => { // six broken hexagon strokes (the Huge tier's mark)
    for (let k = 0; k < 6; k++) {
      const a0 = (k / 6) * TAU, a1 = ((k + 1) / 6) * TAU, P = (a: number) => [Math.cos(a) * R, Math.sin(a) * R];
      const [x0, y0] = P(a0), [x1, y1] = P(a1);
      inked(() => { g.moveTo(x0 + (x1 - x0) * 0.16, y0 + (y1 - y0) * 0.16); g.lineTo(x0 + (x1 - x0) * 0.84, y0 + (y1 - y0) * 0.84); }, 22, '#ffffff');
    }
  });
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
  return t;
}

/** instanced atlas quads: per instance matrix, tint (instanceColor) and (cell, alpha); 4×`rows` atlas cells (rows 2 unless told). Also used by telegraphs.ts. */
export class AtlasQuads {
  readonly mesh: THREE.InstancedMesh;
  private ca: THREE.InstancedBufferAttribute; n = 0;
  constructor(geo: THREE.BufferGeometry, tex: THREE.Texture, readonly cap: number, additive: boolean, renderOrder: number, rows = 2) {
    this.ca = new THREE.InstancedBufferAttribute(new Float32Array(cap * 2), 2); this.ca.setUsage(THREE.DynamicDrawUsage);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const a = instancedBufferAttribute(this.ca) as any;
    const cell = a.x, col = cell.mod(4), row = cell.div(4).floor();
    const tx = texture(tex, uv().mul(vec2(0.25, 1 / rows)).add(vec2(col.mul(0.25), float(1 - 1 / rows).sub(row.mul(1 / rows)))));
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending });
    mat.colorNode = tx.rgb; mat.opacityNode = tx.a.mul(a.y);
    this.mesh = new THREE.InstancedMesh(geo, mat, cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = renderOrder;
  }
  push(m: THREE.Matrix4, cell: number, alpha: number, r = 1, g = 1, b = 1): void {
    if (this.n >= this.cap || alpha <= 0.004) return;
    const i = this.n++; this.mesh.setMatrixAt(i, m);
    const A = this.ca.array as Float32Array; A[i * 2] = cell; A[i * 2 + 1] = alpha;
    const C = this.mesh.instanceColor!.array as Float32Array; C[i * 3] = r; C[i * 3 + 1] = g; C[i * 3 + 2] = b;
  }
  end(): void {
    this.mesh.count = this.n; this.mesh.visible = this.n > 0;
    if (!this.n) return;
    this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true; this.ca.needsUpdate = true;
  }
}

// ---------------------------------------------------------------- the renderer ----------------------------------------------------------------
/**
 * Render-only readability scale on top of P1's metre sizes (Small, Medium, Large, Salve, Sweep). At the real camera
 * (distance 25, 55°, 1280×720, medium render scale) the literal sizes made Small/Medium XP a few-pixel speck, far smaller than
 * the classic gems; P1's QA rule is to fix legibility with render-only scale/contrast before adding particles or bloom.
 * Collision is untouched. Set all to 1 for P1's exact sizes. (P1's own floor rule caps at 1.35 / 1.25: these exceed it slightly.)
 */
export const READ_SCALE = [2.06, 1.93, 1.79, 1.4, 1.35, 1.4]; // 2026-11-04 (owner + P1, bright maps): small / medium / large XP gems +25% (were 1.65 / 1.54 / 1.43; before that 1.5 / 1.4 / 1.3) · [5] = the Huge XP spire
const XP_CAP = GEM_POOL, GHOSTS = 96, BURSTS = 64, MARK_XP = 32, STREAKS = 48, GLINTS = 24;
/** body index per sim kind (−1 = not ours: the Array Seal stays on the classic path). A Large gem worth HUGE_XP+ uses body 5 (see bodyOf) */
const BODY_OF = [0, 1, 2, 3, -1, 4];
const BODIES = 6, HUGE_BODY = 5;
/** sparkle: seconds between glints per tier (larger tiers glint more often) and the peak added light (kept under the hit sparks / telegraphs) */
const GLINT_PERIOD = [1.9, 1.6, 1.1, 0.85], GLINT_PEAK = [0.4, 0.45, 0.55, 0.65];
const WHITE = new THREE.Color(1, 1, 1);
/** XP tier (0 Small … 3 Huge) → body index, and back */
const BODY_OF_TIER = [0, 1, 2, HUGE_BODY], TIER_OF_BODY = [0, 1, 2, -1, -1, 3];
/** the body index of a sim gem (−1 = not ours) */
const bodyOf = (kind: number, value: number): number => { const t = xpTier(kind, value); return t >= 0 ? BODY_OF_TIER[t] : (BODY_OF[kind] ?? -1); };
const BUILD: ((sh: Shades) => PickupLook)[] = [splinter, fork, crown, salve, compass, spire];
const isXpBody = (i: number): boolean => i < 3 || i === HUGE_BODY;
/** all six bodies for the given tier shades (QA / tests: topology never depends on the colours, which `recolor` relies on) */
export const buildLooks = (shades: Shades[]): PickupLook[] => BUILD.map((b, i) => b(shades[TIER_OF_BODY[i]] ?? shades[0]));
/** ground glow pool: ring diameter (m) per tier before READ_SCALE, and its alpha. Small gets none (there are hundreds and they are tiny) */
// (POOL_A: kept for when GEM_GROUND_MARKS is re-enabled)
const POOL_D = [0, 1.1, 1.5, 2.3], POOL_A = [0, 0.8, 0.85, 0.95]; void POOL_A;

export class PickupArt {
  readonly looks: PickupLook[];
  private bodies: THREE.InstancedMesh[] = [];
  /** per-instance (cos yaw, sin yaw, scale) for the spin / ink offsets (see pickupPosition) */
  private inst: THREE.InstancedBufferAttribute[] = [];
  private hulls: (THREE.InstancedMesh | null)[] = [];
  private inks: THREE.Color[] = [];
  /** palette in use (colour-blind mode); a change recolours the facets and tints in place */
  private cbSeen = '';
  /** per tier: light-shade tint (r, g, b, linear 0..1) for marks, pools, slivers */
  private tint = new Float32Array(TIER_COUNT * 3);
  private bn = new Int32Array(BODIES);
  private marks: AtlasQuads; private beacons: AtlasQuads; private vfx: AtlasQuads;
  // per sim gem slot: last frame's state (collections = alive → dead transitions)
  private prevAlive = new Uint8Array(XP_CAP); private prevKind = new Uint8Array(XP_CAP); private prevBody = new Uint8Array(XP_CAP); private prevPos = new Float32Array(XP_CAP * 3);
  private lastSim: Sim | null = null;
  // XP collection: shrink ghosts + sliver bursts
  private ghosts = new Float32Array(GHOSTS * 5); private gn = 0;           // body, x, y, z, t
  private bursts = new Float32Array(BURSTS * 6); private brn = 0;          // x, y, z, t, tier, seed
  private deaths = new Float32Array(XP_CAP * 5); private dn = 0;           // kind, x, y, z, body (this frame)
  // XP ground-mark selection: nearest 32 medium/large
  private candI = new Int32Array(XP_CAP); private candD = new Float32Array(XP_CAP);
  // Salve / Sweep sequences
  private salveT = -1; private salveX = 0; private salveY = 0; private salveZ = 0; private lastSalve = -9;
  private sweepT = -1; private sweepX = 0; private sweepZ = 0; private streakI = new Int32Array(STREAKS); private streakN = -1;
  private sectorD = new Float32Array(STREAKS); private glints = 0; private lastXp = 0;
  private camQ = new THREE.Quaternion(); private flat = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
  private beaconQ = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI / 4, 0));
  private pa = new THREE.Vector3(); private pb = new THREE.Vector3();
  /** debug / QA counters */
  stats = { xp: 0, salve: 0, sweep: 0, marks: 0, beacons: 0, vfx: 0, bursts: 0, coalesced: 0 };

  constructor(scene: THREE.Scene, style: RenderStyle, private groundY: (x: number, z: number) => number) {
    const shades = Array.from({ length: TIER_COUNT }, (_, t) => tierShades(tierColor(t)));
    this.looks = buildLooks(shades);
    const caps = [XP_CAP + GHOSTS, XP_CAP + GHOSTS, XP_CAP + GHOSTS, 8, 8, XP_CAP + GHOSTS];
    this.looks.forEach((L, i) => {
      const k = READ_SCALE[i]; L.geo.scale(k, k, k); L.hover *= k; L.bob *= k;
      const mat = style === 'cel' ? toonMaterial() : new THREE.MeshStandardNodeMaterial({ roughness: 0.45, metalness: 0.2, flatShading: true });
      mat.vertexColors = true;
      const tiltX = i === 3 || i === 4 ? L.tilt : 0; // Salve / Compass tip toward the camera (world X)
      const inst = new THREE.InstancedBufferAttribute(new Float32Array(caps[i] * 4), 4); // (cos yaw, sin yaw, scale, sparkle seed) inst.setUsage(THREE.DynamicDrawUsage);
      L.geo.setAttribute('inst', inst); this.inst.push(inst);
      mat.positionNode = pickupPosition(0, tiltX, 4);
      mat.emissiveNode = isXpBody(i) ? this.gemGlow(TIER_OF_BODY[i]) : attribute('color', 'vec3').mul(attribute('emis', 'float')).mul(0.6);
      const body = new THREE.InstancedMesh(L.geo, mat, caps[i]);
      body.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(caps[i] * 3).fill(1), 3);
      body.frustumCulled = false; body.count = 0; body.visible = false; body.castShadow = false;
      scene.add(body); this.bodies.push(body);
      let hull: THREE.InstancedMesh | null = null;
      if (style === 'cel') {
        const om = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide });
        const inkC = new THREE.Color(L.ink); this.inks[i] = inkC; // (recoloured live with the colour-blind mode)
        om.colorNode = uniform(inkC); om.positionNode = pickupPosition(isXpBody(i) ? 0.034 : 0.038, tiltX, 4);
        const hg = hullGeometry(L.geo); hg.setAttribute('inst', inst);
        hull = new THREE.InstancedMesh(hg, om, caps[i]);
        hull.instanceMatrix = body.instanceMatrix; hull.frustumCulled = false; hull.count = 0; hull.visible = false;
        scene.add(hull);
      }
      this.hulls.push(hull);
    });
    const tex = atlasTexture();
    this.marks = new AtlasQuads(new THREE.PlaneGeometry(1, 1), tex, MARK_XP * 2 + 24, false, 2, ATLAS_ROWS); // (pool + glyph per tracked gem, plus Salve / Sweep marks)
    const cross = new THREE.PlaneGeometry(1, 1); cross.translate(0, 0.5, 0);
    const cross2 = cross.clone().rotateY(Math.PI / 2);
    const bg = new THREE.BufferGeometry(); // two crossed vertical cards, base at the origin
    bg.setAttribute('position', new THREE.Float32BufferAttribute([...cross.getAttribute('position').array, ...cross2.getAttribute('position').array], 3));
    bg.setAttribute('uv', new THREE.Float32BufferAttribute([...cross.getAttribute('uv').array, ...cross2.getAttribute('uv').array], 2));
    const ci = cross.index!.array as ArrayLike<number>; bg.setIndex([...Array.from(ci), ...Array.from(ci, (v) => v + 4)]);
    this.beacons = new AtlasQuads(bg, tex, MARK_XP + 24, false, 4, ATLAS_ROWS);
    this.vfx = new AtlasQuads(new THREE.PlaneGeometry(1, 1), tex, 420, true, 5, ATLAS_ROWS);
    scene.add(this.marks.mesh, this.beacons.mesh, this.vfx.mesh);
    this.cbSeen = CB.mode; this.paletteTints(shades);
  }

  /** sparkle controls (uniforms): amplitude and rate scale, set per frame from Reduce flashing and the crowd size */
  private sparkAmp = uniform(1); private sparkRate = uniform(1);
  /**
   * XP gem emissive (owner 2026-11-02: brighter, a little sparkle, a precious-gem look). All in the gem shader, no extra buffers or particles:
   *  - body glow: facet colour x (facet emissive weight + a small floor), so no facet goes dull and Glow Off / Low still reads;
   *  - rim: a Fresnel edge of light colour, which is what makes the cut read as a translucent jewel with a deeper core;
   *  - highlight: a fixed-light specular on up-facing facets that slides across as the gem spins and bobs (stronger with value);
   *  - glints: every `period` s (desynchronised by the per-instance seed) a brief pulse lights a random light facet; the larger tiers glint more.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private gemGlow(tier: number): any {
    const col = attribute('color', 'vec3'), em = attribute('emis', 'float'), seed = attribute('inst', 'vec4').w;
    const base = col.mul(em.mul(0.95).add(0.1)).mul(0.9);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ndv: any = normalView.dot(positionViewDirection).abs();
    const rim = pow(float(1).sub(ndv), 2.4).mul(mix(col, vec3(1, 1, 1), 0.55)).mul(0.3 + 0.07 * tier);
    const spec = pow(dot(normalWorld, vec3(0.34, 0.9, 0.28)).max(0), 18).mul(0.28 + 0.12 * tier);
    const period = GLINT_PERIOD[tier], dur = 0.16 / period;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cyc: any = time.mul(this.sparkRate).div(period).add(seed.mul(7)), ph = fract(cyc);
    const pulse = smoothstep(0, dur * 0.3, ph).mul(float(1).sub(smoothstep(dur * 0.3, dur, ph)));
    const facet = step(0.62, fract(em.mul(17.3).add(floor(cyc).mul(0.618)).add(seed.mul(5.1)))).mul(smoothstep(0.25, 0.4, em));
    const glint = vec3(1, 0.96, 0.85).mul(pulse.mul(facet).mul(this.sparkAmp).mul(GLINT_PEAK[tier]));
    return base.add(rim).add(spec.mul(vec3(1, 1, 1))).add(glint);
  }
  private paletteTints(shades: Shades[]): void {
    shades.forEach((sh, t) => { _c.setHex(sh.body).lerp(WHITE, 0.22); this.tint[t * 3] = _c.r; this.tint[t * 3 + 1] = _c.g; this.tint[t * 3 + 2] = _c.b; });
  }
  /** colour-blind mode changed: rewrite the XP facet colours (same topology, so only the colour attribute), the ink hulls and the tints */
  private recolor(): void {
    const shades = Array.from({ length: TIER_COUNT }, (_, t) => tierShades(tierColor(t)));
    BODY_OF_TIER.forEach((bi, t) => {
      const fresh = BUILD[bi](shades[t]).geo, dst = this.looks[bi].geo.getAttribute('color') as THREE.BufferAttribute;
      (dst.array as Float32Array).set(fresh.getAttribute('color').array as Float32Array); dst.needsUpdate = true; fresh.dispose();
      this.inks[bi]?.setHex(shades[t].ink);
    });
    this.paletteTints(shades); this.cbSeen = CB.mode;
  }
  /** sim gem kinds this renderer draws (the classic gem loop skips them) */
  owns(kind: number): boolean { return BODY_OF[kind] >= 0; }
  /** QA: the tier of a gem the way this renderer draws it (0 Small … 3 Huge, −1 not XP) */
  tierOf(kind: number, value: number): number { return xpTier(kind, value); }

  private body(i: number, x: number, y: number, z: number, q: THREE.Quaternion, s: number, bright: number, yaw: number, seed = 0): void {
    const n = this.bn[i]; if (n >= this.bodies[i].instanceMatrix.count) return;
    _p.set(x, y, z); _s.set(s, s, s); this.bodies[i].setMatrixAt(n, _m.compose(_p, q, _s));
    const C = this.bodies[i].instanceColor!.array as Float32Array; C[n * 3] = C[n * 3 + 1] = C[n * 3 + 2] = bright;
    const I = this.inst[i].array as Float32Array; I[n * 4] = Math.cos(yaw); I[n * 4 + 1] = Math.sin(yaw); I[n * 4 + 2] = s; I[n * 4 + 3] = seed;
    this.bn[i] = n + 1;
  }
  private flatMark(x: number, z: number, d: number, cell: number, alpha: number, yaw: number, lift = 0.04, r = 1, g = 1, b = 1): void {
    _q.setFromAxisAngle(_Y, yaw).multiply(this.flat);
    _p.set(x, this.groundY(x, z) + lift, z); _s.set(d, d, 1);
    this.marks.push(_m.compose(_p, _q, _s), cell, alpha, r, g, b);
  }
  /** Huge gold glint: a slow four-point twinkle (rate and depth follow Reduce flashing) */
  private glint(x: number, y: number, z: number, ph: number, t: number): void {
    if (this.glints >= GLINTS) return;
    this.glints++;
    const w = Math.sin(t * FLASH.rate(4.2) + ph), k = 1 + 0.22 * FLASH.flick(1) * w; // 1 ± 0.22 (less when flashes are reduced)
    this.bill(x, y, z, 0.8 * k, 0.8 * k, t * 0.6 + ph, CELL.star, 0.78 + 0.22 * FLASH.flick(1) * w, 1, 0.84, 0.36);
  }
  private beacon(x: number, y: number, z: number, h: number, w: number, alpha: number, r: number, g: number, b: number): void {
    _p.set(x, y, z); _s.set(w, h, w);
    this.beacons.push(_m.compose(_p, this.beaconQ, _s), CELL.beacon, alpha, r, g, b);
  }
  /** camera-facing quad rolled by `roll` (screen radians) */
  private bill(x: number, y: number, z: number, sx: number, sy: number, roll: number, cell: number, alpha: number, r = 1, g = 1, b = 1): void {
    _q2.setFromAxisAngle(_z, roll); _q.copy(this.camQ).multiply(_q2);
    _p.set(x, y, z); _s.set(sx, sy, 1);
    this.vfx.push(_m.compose(_p, _q, _s), cell, alpha, r, g, b);
  }
  /** screen-space angle of the world segment a → b */
  private screenAngle(cam: THREE.Camera, ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
    this.pa.set(ax, ay, az).project(cam); this.pb.set(bx, by, bz).project(cam);
    return Math.atan2(this.pb.y - this.pa.y, (this.pb.x - this.pa.x) * ((cam as THREE.PerspectiveCamera).aspect || 1));
  }

  sync(sim: Sim, t: number, dt: number, cam: THREE.Camera): void {
    if (sim !== this.lastSim) { this.lastSim = sim; this.prevAlive.fill(0); this.gn = 0; this.brn = 0; this.salveT = -1; this.sweepT = -1; }
    if (CB.mode !== this.cbSeen) this.recolor();
    this.camQ.copy(cam.quaternion); this.glints = 0;
    { // sparkle: gentler with Reduce flashing, a static glint with Minimal / reduced motion, and thinner in a crowd (> ~60 gems)
      const n = this.lastXp, crowd = n > 60 ? Math.max(0.3, 60 / n) : 1, lvl = FLASH.mode;
      const still = lvl === 'minimal' || (typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches);
      this.sparkAmp.value = still ? 0.35 : (lvl === 'reduced' ? 0.6 : 1) * crowd; this.sparkRate.value = still ? 0 : (lvl === 'reduced' ? 0.5 : 1) * (0.5 + 0.5 * crowd);
    }
    this.bn.fill(0); this.marks.n = 0; this.beacons.n = 0; this.vfx.n = 0; this.dn = 0;
    const L = this.looks, gems = sim.gems, P = sim.player;
    const heroG = this.groundY(P.x, P.z);
    let cn = 0;
    for (let i = 0; i < gems.length && i < XP_CAP; i++) {
      const g = gems[i];
      if (!g.alive) {
        if (this.prevAlive[i]) { // collected this frame (gems only die on collection)
          const d = this.dn++ * 5; this.deaths[d] = this.prevKind[i]; this.deaths[d + 1] = this.prevPos[i * 3]; this.deaths[d + 2] = this.prevPos[i * 3 + 1]; this.deaths[d + 3] = this.prevPos[i * 3 + 2]; this.deaths[d + 4] = this.prevBody[i];
        }
        this.prevAlive[i] = 0; continue;
      }
      const bi = bodyOf(g.kind, g.value);
      this.prevAlive[i] = bi >= 0 ? 1 : 0; this.prevKind[i] = g.kind; this.prevBody[i] = Math.max(0, bi);
      if (bi < 0) continue;
      const lk = L[bi], ph = i * 2.399, gy = this.groundY(g.x, g.z);
      const y = gy + lk.hover + (g.y - 0.6) + Math.sin(t * lk.bobHz * TAU + ph) * lk.bob;
      _e.set(0, t * lk.yaw + ph, 0); _q.setFromEuler(_e);
      // Splinter: 8° local lean that turns with the yaw · Salve 15° / Compass 18°: tipped toward the camera (world X)
      if (lk.tilt) { if (bi >= 3) { _q2.setFromAxisAngle(_X, lk.tilt); _q.premultiply(_q2); } else { _q2.setFromAxisAngle(_z, lk.tilt); _q.multiply(_q2); } }
      this.body(bi, g.x, y, g.z, _q, 1, 1 + Math.sin(t * lk.pulseHz * TAU + ph) * lk.pulse, t * lk.yaw + ph, (i * 0.618034) % 1);
      this.prevPos[i * 3] = g.x; this.prevPos[i * 3 + 1] = y; this.prevPos[i * 3 + 2] = g.z;
      if (bi === 1 || bi === 2 || bi === HUGE_BODY) { const dx = g.x - P.x, dz = g.z - P.z; this.candI[cn] = i; this.candD[cn++] = (dx * dx + dz * dz) * (bi === HUGE_BODY ? 0.04 : 1); } // (Huge gems are rare and worth the most: they win the mark budget)
      if (bi === HUGE_BODY) { // the gold glint: a four-point twinkle over the tip
        this.glint(g.x, y + 0.66 * READ_SCALE[HUGE_BODY], g.z, ph, t);
      }
      else if (bi === 3) { // Salve: broken ring + cross, 1.4 m tapered red beacon
        this.flatMark(g.x, g.z, 0.88 * READ_SCALE[3], CELL.salve, 0.95, 0);
        this.beacon(g.x, gy + 0.3, g.z, 1.4 + 0.3, 0.32, 0.5, 1, 0.42, 0.5);
      } else if (bi === 4) { // Sweep: compass rose (slowly turning), 1.8 m teal-gold beacon
        this.flatMark(g.x, g.z, 1.15 * READ_SCALE[4], CELL.rose, 0.95, t * 0.5);
        this.beacon(g.x, gy + 0.3, g.z, 1.8 + 0.3, 0.36, 0.55, 0.55, 1, 0.85);
      }
    }
    // nearest 32 medium / large XP get their ground mark (and the large ones their short beacon)
    const keep = Math.min(MARK_XP, cn);
    for (let k = 0; k < keep; k++) {
      let best = k; for (let j = k + 1; j < cn; j++) if (this.candD[j] < this.candD[best]) best = j;
      if (best !== k) { const ti = this.candI[k], td = this.candD[k]; this.candI[k] = this.candI[best]; this.candD[k] = this.candD[best]; this.candI[best] = ti; this.candD[best] = td; }
      const g = gems[this.candI[k]], tier = xpTier(g.kind, g.value), gy = this.groundY(g.x, g.z), tc = tier * 3;
      const tr = this.tint[tc], tg = this.tint[tc + 1], tb = this.tint[tc + 2];
      // owner 2026-11-03: 'get rid of the glow on the ground underneath the gems' — no glow pool and no ground glyph under XP
      // gems (GEM_GROUND_MARKS); the tier still reads from the gem's own shape, colour, sparkle and the Medium / Huge beacons
      if (!GEM_GROUND_MARKS) { /* no ground marks */ }
      else if (tier === 1) this.flatMark(g.x, g.z, 0.43 * READ_SCALE[1], CELL.chevron, 0.72, t * -0.3 + this.candI[k], 0.04, tr, tg, tb);
      else if (tier === 2) this.flatMark(g.x, g.z, 0.68 * READ_SCALE[2], CELL.tri, 0.8, 0, 0.04, tr, tg, tb);
      else if (tier === 3) { this.flatMark(g.x, g.z, POOL_D[3] * 1.35 * READ_SCALE[HUGE_BODY] * 0.7, CELL.pool, 0.4, 0, 0.033, 1, 0.8, 0.3); this.flatMark(g.x, g.z, 1.0 * READ_SCALE[HUGE_BODY], CELL.hex, 0.9, t * 0.35 + this.candI[k], 0.04, tr, tg, tb); }
      if (tier === 2) this.beacon(g.x, gy + 0.3, g.z, 0.7 + 0.3, 0.16, 0.36, tr, tg, tb);
      else if (tier === 3) this.beacon(g.x, gy + 0.3, g.z, 1.9, 0.26, 0.5, 1, 0.82, 0.35); // gold column
    }
    this.collect(sim, t, heroG);
    this.drawVfx(sim, t, dt, cam, heroG);
    // flush
    const st = this.stats; st.xp = this.bn[0] + this.bn[1] + this.bn[2] + this.bn[HUGE_BODY]; this.lastXp = st.xp; st.salve = this.bn[3]; st.sweep = this.bn[4];
    for (let i = 0; i < BODIES; i++) {
      const b = this.bodies[i], n = this.bn[i], h = this.hulls[i];
      b.count = n; b.visible = n > 0; if (h) { h.count = n; h.visible = n > 0; }
      if (n) { b.instanceMatrix.needsUpdate = true; b.instanceColor!.needsUpdate = true; this.inst[i].needsUpdate = true; }
    }
    this.marks.end(); this.beacons.end(); this.vfx.end();
    st.marks = this.marks.n; st.beacons = this.beacons.n; st.vfx = this.vfx.n;
  }

  /** turn this frame's collections into VFX (XP bursts coalesce when > 20 land in one frame) */
  private collect(sim: Sim, t: number, heroG: number): void {
    const dense = this.dn > 20;
    for (let k = 0; k < this.dn; k++) {
      const kind = this.deaths[k * 5], x = this.deaths[k * 5 + 1], y = this.deaths[k * 5 + 2], z = this.deaths[k * 5 + 3], body = this.deaths[k * 5 + 4];
      if (kind <= 2) {
        if (this.gn < GHOSTS) { const o = this.gn++ * 5; this.ghosts[o] = body; this.ghosts[o + 1] = x; this.ghosts[o + 2] = y; this.ghosts[o + 3] = z; this.ghosts[o + 4] = t; }
        let skip = this.brn >= BURSTS;
        if (dense && !skip) for (let b = 0; b < this.brn; b++) { // one visible burst per 0.8 m cell per 100 ms
          const o = b * 6; if (t - this.bursts[o + 3] < 0.1 && Math.abs(this.bursts[o] - x) < 0.8 && Math.abs(this.bursts[o + 2] - z) < 0.8) { skip = true; break; }
        }
        if (skip) { this.stats.coalesced++; continue; }
        const o = this.brn++ * 6; this.bursts[o] = x; this.bursts[o + 1] = y; this.bursts[o + 2] = z; this.bursts[o + 3] = t; this.bursts[o + 4] = TIER_OF_BODY[body]; this.bursts[o + 5] = (x * 7.13 + z * 3.7) % TAU;
        this.stats.bursts++;
      } else if (kind === 3) {
        if (t - this.lastSalve >= 0.2) { this.lastSalve = t; this.salveT = t; this.salveX = x; this.salveY = y; this.salveZ = z; }
      } else if (kind === 5) {
        this.sweepT = t; this.sweepX = sim.player.x; this.sweepZ = sim.player.z; this.streakN = -1;
      }
    }
  }

  private drawVfx(sim: Sim, t: number, _dt: number, cam: THREE.Camera, heroG: number): void {
    const P = sim.player, chestY = heroG + 1.25;
    // XP: 0–45 ms the shard shrinks to 75 % and brightens 1.2×
    for (let k = 0; k < this.gn; k++) {
      const o = k * 5, u = (t - this.ghosts[o + 4]) / 0.045;
      if (u >= 1 || u < 0) { this.gn--; if (k !== this.gn) this.ghosts.copyWithin(o, this.gn * 5, this.gn * 5 + 5); k--; continue; }
      _q.identity(); this.body(this.ghosts[o], this.ghosts[o + 1], this.ghosts[o + 2], this.ghosts[o + 3], _q, 1 - 0.25 * u, 1.2, 0);
    }
    // XP: 45–105 ms three radial slivers grow 0.08 → 0.28 m; 105–160 ms they fade
    for (let k = 0; k < this.brn; k++) {
      const o = k * 6, a = t - this.bursts[o + 3];
      if (a >= 0.16) { this.brn--; if (k !== this.brn) this.bursts.copyWithin(o, this.brn * 6, this.brn * 6 + 6); k--; continue; }
      if (a < 0.12) { const tt = this.bursts[o + 4] | 0, u = a / 0.12; this.bill(this.bursts[o], this.bursts[o + 1], this.bursts[o + 2], (0.45 + 0.2 * tt) * (0.6 + 0.4 * Math.sin(u * Math.PI)), (0.45 + 0.2 * tt) * (0.6 + 0.4 * Math.sin(u * Math.PI)), this.bursts[o + 5], CELL.star, (1 - u) * 0.85 * FLASH.dim, tt === 3 ? 1 : this.tint[tt * 3] + 0.3, tt === 3 ? 0.85 : this.tint[tt * 3 + 1] + 0.3, tt === 3 ? 0.4 : this.tint[tt * 3 + 2] + 0.3); } // chime-glint
      if (a < 0.045) continue;
      const grow = Math.min(1, (a - 0.045) / 0.06), len = (0.08 + 0.2 * grow) * 1.5, fade = a < 0.105 ? 1 : 1 - (a - 0.105) / 0.055;
      const tc = (this.bursts[o + 4] | 0) * 3, c = this.tint.subarray(tc, tc + 3);
      for (let j = 0; j < (this.bursts[o + 4] === 3 ? 5 : 3); j++) { // (Huge throws five slivers)
        const r = this.bursts[o + 5] + (j / (this.bursts[o + 4] === 3 ? 5 : 3)) * TAU, d = 0.06 + len * 0.6;
        // offset along the screen-space ray: right/up of the camera
        _a.set(Math.cos(r) * d, Math.sin(r) * d, 0).applyQuaternion(this.camQ);
        this.bill(this.bursts[o] + _a.x, this.bursts[o + 1] + _a.y, this.bursts[o + 2] + _a.z, len, len * 0.25, r, CELL.sliver, 0.9 * fade, c[0], c[1], c[2]);
      }
    }
    // Salve: 0–90 ms cross flash · 90–270 ms four ribbons curve into the chest · 270–360 ms a small ivory cross above the hero
    if (this.salveT >= 0) {
      const a = t - this.salveT;
      if (a > 0.36) this.salveT = -1;
      else {
        if (a < 0.09) { const u = a / 0.09; this.bill(this.salveX, this.salveY + 0.25, this.salveZ, 0.55 + 0.35 * u, 0.55 + 0.35 * u, 0, CELL.cross, 1 - u * u); }
        if (a >= 0.07 && a < 0.29) {
          const u = Math.min(1, (a - 0.07) / 0.18);
          for (let r = 0; r < 4; r++) {
            const side = (r / 4) * TAU + 0.4;
            for (let s = 0; s < 3; s++) {
              const v = Math.max(0, u - s * 0.12), e = 1 - (1 - v) * (1 - v), bulge = Math.sin(v * Math.PI) * 0.7;
              const x = this.salveX + (P.x - this.salveX) * e + Math.cos(side) * bulge, z = this.salveZ + (P.z - this.salveZ) * e + Math.sin(side) * bulge;
              const y = this.salveY + (chestY - this.salveY) * e + Math.sin(side * 2) * bulge * 0.3;
              const v2 = Math.min(1, v + 0.05), e2 = 1 - (1 - v2) * (1 - v2), b2 = Math.sin(v2 * Math.PI) * 0.7;
              const ang = this.screenAngle(cam, x, y, z, this.salveX + (P.x - this.salveX) * e2 + Math.cos(side) * b2, this.salveY + (chestY - this.salveY) * e2 + Math.sin(side * 2) * b2 * 0.3, this.salveZ + (P.z - this.salveZ) * e2 + Math.sin(side) * b2);
              const k2 = 1 - s * 0.3;
              this.bill(x, y, z, 0.6 * k2, 0.14 * k2, ang, CELL.sliver, (1 - u * 0.4) * k2, 1, 0.53 + 0.42 * v, 0.57 + 0.27 * v);
            }
          }
        }
        if (a >= 0.27) { const u = (a - 0.27) / 0.09; this.bill(P.x, heroG + 2.35 + u * 0.15, P.z, 0.42, 0.42, 0, CELL.cross, 1 - u); }
      }
    }
    // Sweep: 0–280 ms four-point ground wave 0.6 → 5 m · compass after-image at the hero for 200 ms · 120–650 ms ≤ 48 streaks on real converging XP
    if (this.sweepT >= 0) {
      const a = t - this.sweepT;
      if (a > 0.65) this.sweepT = -1;
      else {
        if (a < 0.28) { const u = a / 0.28, r = 0.6 + 4.4 * (1 - (1 - u) * (1 - u)); this.flatMark(this.sweepX, this.sweepZ, r * 2 * 1.25, CELL.rose, 0.85 * (1 - u), u * 0.8, 0.06); }
        if (a < 0.3) {
          const s = a < 0.2 ? 1.15 : 1.15 * (1 - (a - 0.2) / 0.1);
          const sy = t * 0.5; _e.set(0, sy, 0); _q.setFromEuler(_e); _q2.setFromAxisAngle(_X, 18 * Math.PI / 180); _q.premultiply(_q2);
          this.body(4, P.x, heroG + 2.2, P.z, _q, Math.max(0.01, s), 1.3, sy);
        }
        if (a >= 0.12) {
          if (this.streakN < 0) this.pickStreaks(sim);
          const fade = Math.min(1, (a - 0.12) / 0.08) * (a > 0.55 ? 1 - (a - 0.55) / 0.1 : 1);
          for (let k = 0; k < this.streakN; k++) {
            const g = sim.gems[this.streakI[k]]; if (!g || !g.alive || g.kind > 2) continue;
            const tier = xpTier(g.kind, g.value), gy = this.groundY(g.x, g.z) + this.looks[BODY_OF_TIER[tier]].hover + (g.y - 0.6);
            const dx = P.x - g.x, dz = P.z - g.z, d = Math.hypot(dx, dz) || 1;
            const tail = Math.min(1.4, 0.5 + d * 0.05), tx = g.x - (dx / d) * tail * 0.5, tz = g.z - (dz / d) * tail * 0.5;
            const ang = this.screenAngle(cam, tx, gy, tz, g.x, gy, g.z);
            this.bill(tx, gy, tz, tail, 0.12, ang, CELL.sliver, 0.85 * fade, this.tint[tier * 3], this.tint[tier * 3 + 1], this.tint[tier * 3 + 2]);
          }
        }
      }
    }
  }

  /** a deterministic, spatially spread subset: the farthest pulled XP mote in each of 48 angular sectors around the hero */
  private pickStreaks(sim: Sim): void {
    const P = sim.player; this.streakI.fill(-1); this.sectorD.fill(-1);
    for (let i = 0; i < sim.gems.length; i++) {
      const g = sim.gems[i]; if (!g.alive || g.kind > 2 || !g.pull) continue;
      const dx = g.x - P.x, dz = g.z - P.z, d = dx * dx + dz * dz; if (d < 4) continue;
      const s = Math.min(STREAKS - 1, Math.floor(((Math.atan2(dz, dx) + Math.PI) / TAU) * STREAKS));
      if (d > this.sectorD[s]) { this.sectorD[s] = d; this.streakI[s] = i; }
    }
    let n = 0; for (let s = 0; s < STREAKS; s++) if (this.streakI[s] >= 0) this.streakI[n++] = this.streakI[s];
    this.streakN = n;
  }

  /** render-only readability scale per type (QA readout; index 5 = the Huge XP spire) */
  get readScale(): number[] { return READ_SCALE; }
  /** triangles per pickup type (QA readout) */
  get triCounts(): number[] { return this.looks.map((l) => l.tris); }
}
